// src/components/BeheerdersportaalSidebar.js - Updated with FAQ Admin link
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import '../css/BeheerdersportaalSidebar.css';
import LogoPolderzicht from '../assets/logo_polderzicht_kantoorlaag.png';
import SyncStatusIndicator from './SyncStatusIndicator';
import { getStoredAllowedPages } from '../config/pages';


const BeheerdersportaalSidebar = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const navigate = useNavigate();
  const location = useLocation();

  // Pagina-rechten van de ingelogde gebruiker: lege lijst = volledige toegang.
  const allowedPages = getStoredAllowedPages();
  const hasFullAccess = allowedPages.length === 0;
  const canSee = (key) => hasFullAccess || allowedPages.includes(key);

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('loginTimestamp');
    localStorage.removeItem('userRole');
    localStorage.removeItem('allowedPages');
    // Redirect to home page, not login
    navigate('/home');
  };

  // Function to handle external links
  const openExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // SVG icons for improved visuals
  const icons = {
    home: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9 22 9 12 15 12 15 22"></polyline>
      </svg>
    ),
    dashboard: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"></rect>
        <rect x="14" y="3" width="7" height="7"></rect>
        <rect x="14" y="14" width="7" height="7"></rect>
        <rect x="3" y="14" width="7" height="7"></rect>
      </svg>
    ),
    newsletter: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
        <polyline points="22,6 12,13 2,6"></polyline>
      </svg>
    ),
    energy: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
      </svg>
    ),
    residents: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
    ),
    documents: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
    ),
    maintenance: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
      </svg>
    ),
    meeting: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
      </svg>
    ),
    finances: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23"></line>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
      </svg>
    ),
    faq: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
        <line x1="12" y1="17" x2="12.01" y2="17"></line>
      </svg>
    ),
    logout: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
        <polyline points="16 17 21 12 16 7"></polyline>
        <line x1="21" y1="12" x2="9" y2="12"></line>
      </svg>
    ),
    menuOpen: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12"></line>
        <polyline points="12 19 5 12 12 5"></polyline>
      </svg>
    ),
    menuClosed: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="12" x2="21" y2="12"></line>
        <line x1="3" y1="6" x2="21" y2="6"></line>
        <line x1="3" y1="18" x2="21" y2="18"></line>
      </svg>
    ),

    analytics: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),

        users: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
    ),
key: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
</svg>
  ),
    inspection: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4"></path>
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
      </svg>
    ),
    settings: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"></circle>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
      </svg>
    ),
    doc: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
        <polyline points="13 2 13 9 20 9"></polyline>
      </svg>
    ),
  };

  return (
    <div className={`beheer-wrapper ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
      {/* Sidebar */}
      <aside className="sidebar">
      <div className="sidebar-header">
  <Link to="/home" className="logo-link">
    <img src={LogoPolderzicht} alt="Polderzicht Logo" className="sidebar-logo" />
    <h2 className="sidebar-title">VVE Polderzicht</h2>
  </Link>
</div>
        <nav className="sidebar-nav">
          <ul>
            <li>
              <Link to="/home" className={`nav-link ${location.pathname === '/home' ? 'active' : ''}`}>
                <span className="icon">{icons.home}</span>
                <span className="menu-text">Home</span>
              </Link>
            </li>
            {canSee('beheer') && (
            <li>
              <Link to="/beheer" className={`nav-link ${location.pathname === '/beheer' ? 'active' : ''}`}>
                <span className="icon">{icons.dashboard}</span>
                <span className="menu-text">Beheerdersportaal</span>
              </Link>
            </li>
            )}
            {canSee('newsletter') && (
            <li>
              <Link to="/newsletter" className={`nav-link ${location.pathname === '/newsletter' ? 'active' : ''}`}>
                <span className="icon">{icons.newsletter}</span>
                <span className="menu-text">Nieuwsbrieven</span>
              </Link>
            </li>
            )}
            {canSee('energie') && (
            <li>
              <Link to="/energie" className={`nav-link ${location.pathname.startsWith('/energie') ? 'active' : ''}`}>
                <span className="icon">{icons.energy}</span>
                <span className="menu-text">Energieverbruik</span>
              </Link>
            </li>
            )}
            {canSee('bewoners') && (
            <li>
              <Link to="/bewoners" className={`nav-link ${location.pathname === '/bewoners' ? 'active' : ''}`}>
                <span className="icon">{icons.residents}</span>
                <span className="menu-text">Bewonersbeheer</span>
              </Link>
            </li>
            )}
            {canSee('opnamestaten') && (
            <li>
              <Link to="/opnamestaten" className={`nav-link ${location.pathname.startsWith('/opnamestaten') ? 'active' : ''}`}>
                <span className="icon">{icons.inspection}</span>
                <span className="menu-text">Opnamestaten</span>
              </Link>
            </li>
            )}
            {/* Add the new FAQ Admin link */}
            {canSee('faq-admin') && (
            <li>
              <Link to="/faq-admin" className={`nav-link ${location.pathname === '/faq-admin' ? 'active' : ''}`}>
                <span className="icon">{icons.faq}</span>
                <span className="menu-text">FAQ Beheer</span>
              </Link>
            </li>
            )}
            {canSee('planning-admin') && (
            <li>
              <Link to="/planning-admin" className={`nav-link ${location.pathname.startsWith('/planning-admin') ? 'active' : ''}`}>
                <span className="icon">{icons.meeting}</span>
                <span className="menu-text">Planning Projecten</span>
              </Link>
            </li>
            )}
            {canSee('cv-controle-admin') && (
            <li>
              <Link to="/cv-controle-admin" className={`nav-link ${location.pathname === '/cv-controle-admin' ? 'active' : ''}`}>
                <span className="icon">{icons.maintenance}</span>
                <span className="menu-text">CV-controle meldingen</span>
              </Link>
            </li>
            )}
            {canSee('schilderwerk') && (
            <li>
              <Link to="/schilderwerk" className={`nav-link ${location.pathname.startsWith('/schilderwerk') ? 'active' : ''}`}>
                <span className="icon">{icons.maintenance}</span>
                <span className="menu-text">Schilderwerk balkons</span>
              </Link>
            </li>
            )}
            {canSee('schilder') && (
            <li>
              <Link to="/schilder" className={`nav-link ${location.pathname === '/schilder' ? 'active' : ''}`}>
                <span className="icon">{icons.maintenance}</span>
                <span className="menu-text">Huismeester-app</span>
              </Link>
            </li>
            )}

            {canSee('schoonmaak-admin') && (
            <li>
              <Link to="/schoonmaak-admin" className={`nav-link ${location.pathname === '/schoonmaak-admin' ? 'active' : ''}`}>
                <span className="icon">{icons.inspection}</span>
                <span className="menu-text">Schoonmaak</span>
              </Link>
            </li>
            )}

            {canSee('analytics') && (
            <li>
  <Link to="/analytics" className={`nav-link ${location.pathname === '/analytics' ? 'active' : ''}`}>
    <span className="icon">{icons.analytics}</span>
    <span className="menu-text">Website Analytics</span>
  </Link>
</li>
            )}

            {canSee('user-management') && (
<li>
  <Link to="/user-management" className={`nav-link ${location.pathname === '/user-management' ? 'active' : ''}`}>
   <span className="icon">{icons.users}</span>
    <span className="menu-text">Gebruikersbeheer</span>
  </Link>
</li>
            )}

            {hasFullAccess && (
            <li>
              <span
                className="nav-link"
                onClick={() => openExternalLink('https://drive.google.com/drive/folders/1TFKmSBInXgNffaT7jilr5Dgnko_UqVhw?usp=drive_link')}
              >
                <span className="icon">{icons.documents}</span>
                <span className="menu-text">Documenten (ext)</span>
              </span>
            </li>
            )}
            {hasFullAccess && (
            <li>
              <span
                className="nav-link"
                onClick={() => openExternalLink('https://trello.com/b/k3YcVf6R/')}
              >
                <span className="icon">{icons.maintenance}</span>
                <span className="menu-text">Onderhoud (ext)</span>
              </span>
            </li>
            )}
            {hasFullAccess && (
            <li>
              <span
                className="nav-link"
                onClick={() => openExternalLink('https://trello.com/b/iCyt3RWV/sleutels')}
              >
                <span className="icon">{icons.key}</span>
                <span className="menu-text">Sleutels (ext)</span>
              </span>
            </li>
            )}

          </ul>
        </nav>
        
        {/* Logout button */}
        <div className="sidebar-footer">
          <button 
            onClick={handleLogout} 
            className="logout-button"
          >
            <span className="icon">{icons.logout}</span>
            <span className="menu-text">Uitloggen</span>
          </button>
        </div>
      </aside>
      
      {/* Mobile sidebar toggle */}
      <button 
        className="sidebar-toggle" 
        onClick={toggleSidebar}
        aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
      >
        {sidebarOpen ? icons.menuOpen : icons.menuClosed}
      </button>
      
      {/* Mobile backdrop overlay */}
      {sidebarOpen && window.innerWidth <= 768 && 
        <div className="mobile-backdrop" onClick={toggleSidebar}></div>
      }

      {/* Render children components */}
      {children}
    </div>
  );
};

export default BeheerdersportaalSidebar;